import { motion } from "framer-motion";
import SpinningBadge from "@/components/SpinningBadge";

const NOTES = [
    {
        n: "01",
        label: "Cutting",
        body: "Patterns drafted by hand, prototyped three times, refined until silence — the cleanest expression wins.",
    },
    {
        n: "02",
        label: "Fabric",
        body: "Sourced from heritage mills. Heavyweight cotton, garment-dyed, sun-cured. The hand-feel is the brief.",
    },
    {
        n: "03",
        label: "Finish",
        body: "Each piece bagged, numbered, sealed. The serial is your proof of presence.",
    },
];

export default function Studio() {
    return (
        <section
            id="studio"
            data-testid="studio-section"
            className="relative bg-black py-24 lg:py-40 border-t border-white/10 overflow-hidden"
        >
            {/* Massive faded section number */}
            <p
                aria-hidden
                className="absolute -bottom-10 left-4 lg:left-12 font-display text-[14rem] lg:text-[22rem] leading-none text-outline opacity-[0.10] select-none pointer-events-none tabular-nums"
            >
                04
            </p>

            <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
                <div className="grid grid-cols-12 gap-6 mb-20 items-start">
                    <div className="col-span-12 md:col-span-3">
                        <p className="label-mono">— 04 / Studio</p>
                    </div>
                    <div className="col-span-12 md:col-span-9 relative">
                        <h2 className="font-display text-5xl md:text-7xl lg:text-[9rem] uppercase tracking-[-0.03em] leading-[0.85]">
                            Made
                            <br />
                            <span className="text-outline italic font-normal">slowly</span>
                            <span className="font-display">,</span>
                            <br />
                            on purpose.
                        </h2>
                    </div>
                </div>

                {/* Type-forward layout with rotating badge instead of stock photo */}
                <div className="grid grid-cols-12 gap-6 lg:gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                        className="col-span-12 md:col-span-5 flex flex-col items-center md:items-start gap-8"
                    >
                        <SpinningBadge
                            size={260}
                            duration={22}
                            text="ENERGY MATCHER · CRAFTED SLOW · ENERGY MATCHER · CRAFTED SLOW · "
                        />
                        <div className="border border-white/10 px-6 py-4 grid grid-cols-2 gap-x-6 gap-y-2 self-stretch">
                            <p className="label-mono">Lead Time</p>
                            <p className="font-mono text-xs uppercase tracking-[0.2em] text-right">
                                ~14 weeks
                            </p>
                            <p className="label-mono">Production</p>
                            <p className="font-mono text-xs uppercase tracking-[0.2em] text-right">
                                Volume limited
                            </p>
                            <p className="label-mono">Restock</p>
                            <p className="font-mono text-xs uppercase tracking-[0.2em] text-right">
                                Never · Numbered
                            </p>
                        </div>
                    </motion.div>

                    <div className="col-span-12 md:col-span-7 space-y-10">
                        {NOTES.map((n, i) => (
                            <motion.div
                                key={n.n}
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.7,
                                    delay: i * 0.15,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                className="grid grid-cols-12 gap-4 items-start border-t border-white/15 pt-6"
                            >
                                <p className="col-span-2 font-display text-4xl md:text-5xl tabular-nums">
                                    {n.n}
                                </p>
                                <div className="col-span-10">
                                    <p className="label-mono mb-2">{n.label}</p>
                                    <p className="font-display text-2xl md:text-3xl leading-tight">
                                        {n.body}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Quote band */}
                <motion.blockquote
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                    className="mt-32 mx-auto max-w-5xl text-center relative"
                >
                    <span className="absolute -top-16 left-1/2 -translate-x-1/2 font-display text-[8rem] md:text-[14rem] leading-none text-outline opacity-30 select-none pointer-events-none">
                        “
                    </span>
                    <p className="relative font-display text-3xl md:text-5xl uppercase tracking-tight leading-[1.1]">
                        The brand should disappear.
                        <br />
                        <span className="italic text-muted-foreground">
                            What stays is the energy you brought into the room.
                        </span>
                    </p>
                    <p className="label-mono mt-8">— EM, Founder's Note</p>
                </motion.blockquote>
            </div>
        </section>
    );
}
